import {
  CONTENT_SKILLS,
  MOTOR_SKILLS,
  isContentSkill,
  type ContentSkill,
  type MotorSkill,
  type Skill,
} from "../../../shared/types/prisma-enums";
import type {
  ContentSkillRow,
  MotorSkillRow,
} from "../../../shared/api/parent";
/* Deep import, not the barrel — the same reason `SkillProgress` gives: this
   module needs one integer, not five question banks. */
import { LEVEL_COUNT } from "../../../shared/curriculum/progress";

/**
 * C1's *"two grouped skill sections, never one flat list"* (design-system.md §3),
 * as a pure fold.
 *
 * 🔴 THE RULE THIS FILE EXISTS TO KEEP HONEST:
 *
 *   Content skills read as a LEVEL       — "Numbers · Level 3 of 3"
 *   Motor skills read as a PROPORTION    — "exercised in 8 of 10 sessions"
 *
 * `SkillProgress` already enforces the first half structurally: it has no
 * `variant` prop and derives the family from the skill itself, so *"a caller
 * cannot render Numeracy as a percentage even by accident"*. **Do not give it
 * one.** This module is the other half — the arithmetic and the sentence — and
 * it is pure because this repo has no DOM test renderer (OD-60), so every
 * branching decision that would otherwise hide in JSX lives here.
 *
 * 🔴 THE DENOMINATOR GOES ON SCREEN. Contract D4, taking OD-39's own default:
 * *"percentage of sessions in which the skill was exercised"* — plus the
 * requirement that makes it defensible, which is that the parent can see what
 * it is a percentage **of**. `81%` alone is a measurement claim about a child's
 * physical development, and the first question a school asks is *"81% of
 * what?"*
 */

export type Lang = "en";

/** Human labels. Localised here so the components stay dumb. */
export const SKILL_LABEL: Record<Skill, Record<Lang, string>> = {
  NUMERACY: { en: "Numbers" },
  LITERACY: { en: "Letters & words" },
  GENERAL_AWARENESS: { en: "The world around us" },
  SOCIAL_AWARENESS: { en: "Getting on with others" },
  MATCHING: { en: "Matching & sorting" },
  COORDINATION: { en: "Coordination" },
  BALANCE: { en: "Balance" },
  BODY_AWARENESS: { en: "Body awareness" },
};

export interface ContentSection {
  skill: ContentSkill;
  label: string;
  /**
   * 1–3 (ADR-037), or 0 for "not started". Never a percentage.
   *
   * 🔴 Clamped to `LEVEL_COUNT` in the fold — see the note there. Older events
   * carry levels 4 and 5, and this field is the numerator of a fraction a parent
   * reads.
   */
  level: number;
  started: boolean;
}

export interface MotorSection {
  skill: MotorSkill;
  label: string;
  /** The value `SkillProgress` draws — 0–100. */
  percent: number;
  /** 🔴 Rendered beside the bar, always. "exercised in 8 of 10 sessions". */
  caption: string;
  /** True when there are no sessions at all, so the caller can suppress the bar. */
  noData: boolean;
}

export interface SkillSections {
  content: ContentSection[];
  motor: MotorSection[];
}

const CAPTION: Record<
  Lang,
  { some: (n: number, of: number) => string; none: string }
> = {
  en: {
    some: (n, of) => `Exercised in ${String(n)} of ${String(of)} sessions`,
    /*
     * 🔴 NOT "0%". A child who has not played yet, and a child on the TAP path
     * who emits no motor events by design (OD-32), would both read as a zero
     * bar — and a zero on a parent's phone under the words "body awareness" is
     * a statement about their child that we did not make and cannot support.
     */
    none: "No sessions recorded yet",
  },
};

/**
 * 🔴 THE ONE PLACE A MOTOR PROPORTION BECOMES A NUMBER.
 *
 * Integer, because `formatPercent` rounds and *"58.99999999999997%" is exactly
 * the bug that reached a paying parent"* (BUILD-BRIEF §7). Zero sessions is
 * **0 with `noData: true`**, never `NaN` — dividing by an empty denominator is
 * how a bar renders `NaN%` on the screen designed for a 30-second read.
 */
export function motorPercent(exercised: number, played: number): number {
  if (played <= 0) return 0;
  // Clamped, because a numerator larger than its denominator is a bug upstream
  // and a bar past 100% is a bug the parent sees.
  return Math.min(100, Math.max(0, Math.round((exercised / played) * 100)));
}

/**
 * 🔴 THE CONTENT HALF OF `motorPercent`, AND IT EXISTS BECAUSE ITS ABSENCE
 * PRODUCED INVENTED PROGRESS ABOUT A NAMED CHILD.
 *
 * The bar was computed inline on C2 as
 * `level === 3 ? 100 : level === 2 ? 66 : level === 1 ? 40 : skill === "GENERAL_AWARENESS" ? 20 : …`
 * — so a child at level 0, who had done nothing at all, was drawn with a fifth
 * of a General Awareness bar filled in, and their parent read it as progress.
 * Inline arithmetic on a screen is arithmetic with no test.
 *
 * ⚠️ IT IS THE LEVEL AND ONLY THE LEVEL. The label beside the bar says "Level 1
 * of 3"; `design-system.md` §8's rule is that sibling values must not be able to
 * contradict each other, and the old ladder put a 40% bar next to those words.
 */
export function contentPercent(level: number, of: number = LEVEL_COUNT): number {
  if (of <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((level / of) * 100)));
}

/**
 * What a domain's pill says.
 *
 * 🔴 IT READS THE CHILD'S LEVEL, NOT WHICH SUBJECT IT IS. This was a lookup
 * keyed by DOMAIN: Numeracy always said "In Progress" and Matching always "Not
 * Started", for every child on the platform, whatever they had done — so a child
 * who had finished all three Matching levels was shown "Not Started" to their
 * own parent.
 */
export function contentStatus(level: number, of: number = LEVEL_COUNT): string {
  if (level <= 0) return "Not started";
  return level >= of ? "Completed" : "In progress";
}

export function skillSections(
  input: {
    content: readonly ContentSkillRow[];
    motor: readonly MotorSkillRow[];
    sessionsPlayed: number;
  },
  lang: Lang,
): SkillSections {
  const contentBy = new Map(input.content.map((r) => [r.skill, r]));
  const motorBy = new Map(input.motor.map((r) => [r.skill, r]));
  const t = CAPTION[lang];

  /*
   * 🔴 BUILT FROM THE ENUM, NOT FROM THE PAYLOAD. A skill the child has never
   * touched must appear as a zero bar rather than vanish, or an ABSENT skill
   * and a ZERO skill become indistinguishable — and design-system.md §3 is
   * explicit that retired skills *"leave the enum rather than linger as empty
   * bars on a parent's report"*, which only works if everything still in the
   * enum is drawn.
   */
  const content: ContentSection[] = CONTENT_SKILLS.map((skill) => {
    const row = contentBy.get(skill);
    const raw = row?.levelCompleted ?? 0;
    /*
     * 🔴 CLAMPED TO THE DENOMINATOR THE PARENT IS SHOWN — AND THIS IS A BUG
     * FIXED, NOT A PRECAUTION. A live account read **"Level 4 of 3"** across
     * three of the five skills, with all three segments already full.
     *
     * The number is `MAX(Event.level)` (`skills.service.ts`), and the events
     * table still holds rows written when a level was `1 | 2 | 3 | 4 | 5` —
     * ADR-037 cut the ladder to three afterwards. So the server is reporting
     * what happened and it is `LEVEL_COUNT` that moved; nothing upstream is
     * wrong to fix.
     *
     * ⚠️ CLAMPING IS THE HONEST READING RATHER THAN A COSMETIC ONE. A child who
     * completed the old level 4 has finished everything this game now contains —
     * "Level 3 of 3" is true of them, where "4 of 3" is a sentence with no
     * meaning printed on a parent's phone about their own child. It is done here
     * rather than in `SkillProgress` because the fold is where the number is
     * decided, and the segments and the caption must agree.
     */
    const level = Math.min(Math.max(raw, 0), LEVEL_COUNT);
    return {
      skill,
      label: SKILL_LABEL[skill][lang],
      level,
      started: level > 0 || (row?.events ?? 0) > 0,
    };
  });

  const motor: MotorSection[] = MOTOR_SKILLS.map((skill) => {
    const exercised = motorBy.get(skill)?.sessionsExercised ?? 0;
    const noData = input.sessionsPlayed <= 0;
    return {
      skill,
      label: SKILL_LABEL[skill][lang],
      percent: motorPercent(exercised, input.sessionsPlayed),
      caption: noData ? t.none : t.some(exercised, input.sessionsPlayed),
      noData,
    };
  });

  return { content, motor };
}

/**
 * A guard for the one mistake this whole design is arranged to prevent.
 *
 * ⚠️ It exists so the rule is testable rather than only documented: if someone
 * adds a skill to `CONTENT_SKILLS` and wires it into the motor section, or the
 * reverse, a test fails instead of a percentage appearing under "Numbers".
 */
export const sectionFamiliesAreDisjoint = (sections: SkillSections): boolean =>
  sections.content.every((s) => isContentSkill(s.skill)) &&
  sections.motor.every((s) => !isContentSkill(s.skill));
